import axios from '@/plugins/axios'
import _ from 'lodash'
import * as actionTypes from '@/store/action-types'
import * as mutationTypes from '@/store/mutation-types'
import store from '@/store'
import { CompanyStructure, GlobalState, PeriodicityPlannedEmployee } from '@/types'
import {
  AdditionalFieldsPayloadItem,
  AutomaticPlanningPayload,
  CreateShiftPayload, EditShiftNewPayload,
  EmployeeCapacityPayload,
  EventDropPayload,
  FetchEmployeeListForPlanningPayload,
  FetchEmployeePlaceholderListPayload,
  FetchShiftTemplateListPayload,
  PlanningAssistantPeriodicityPayload,
  PlanningAssistantStartEndPayload,
  PlanPayload,
  ShiftListPayload
} from '@/types/payload'
import {
  ShiftPlanResponse,
  PlanResponse,
  ShiftTemplateResponse,
  ShiftElementResponse,
  EmployeeAvailableForPlanningResponse,
  CreateShiftNewResponse, ShiftDetailResponseItem, GetArbeitenResponse
} from '@/types/service-response'
import { ShiftTemplate } from '@/model/planning/ShiftTemplate'
import { EmployeePlaceholder } from '@/model/planning/EmployeePlaceholder'
import { AutomaticallyPlannedEmployee } from '@/model/planning/AutomaticallyPlannedEmployee'
import { ShiftEditorPlanningPayload } from '@/model/planning/ShiftEditorPlanningPayload'
import { EmployeeAvailableForPlanning } from '@/model/planning/EmployeeAvailableForPlanning'
import { PlannedEmployee } from '@/model/planning/PlannedEmployee'
import { Task } from '@/model/planning/Task'
import { ShiftAvailability } from '@/types/enums'
import { EditShiftPlan } from '@/model/planning/EditShiftPlan'
import { Employee } from '@/model/companyStructure/Employee'
import { EmployeeCapacity } from '@/model/planning/EmployeeCapacity'
import {
  COMPANY_GET_BASIC_COMPANY_STRUCTURE,
  PLANNING_ASSISTANT_GET_EMPLOYEE_LIST_FOR_PLANNING,
  PLANNING_ASSISTANT_GET_TASK_LIST
} from '@/store/getter-types'
import { AdditionalGroup } from '@/model/additional-fields/AdditionalGroup'
import moment from 'moment'

const planningActions = {
  //<editor-fold desc="Planning Assistant Actions">
  [actionTypes.PLANNING_ASSISTANT_RESET_STATE] ({ commit }: { commit: Function }): void {
    commit(mutationTypes.PLANNING_ASSISTANT_RESET_STATE_MUTATION)
  },

  [actionTypes.PLANNING_ASSISTANT_INIT_FROM_PAYLOAD] ({ commit }: { commit: Function }, payload: ShiftEditorPlanningPayload): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_DATA_FROM_PAYLOAD_MUTATION, _.cloneDeep(payload))
  },

  [actionTypes.PLANNING_ASSISTANT_SET_START_DATE] ({ commit }: { commit: Function }, payload: Date): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_START_DATE_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_END_DATE] ({ commit }: { commit: Function }, payload: Date): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_END_DATE_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_START_END_DATA] ({ commit }: { commit: Function }, payload: PlanningAssistantStartEndPayload): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_START_DATE_MUTATION, payload.dateStart)
    commit(mutationTypes.PLANNING_ASSISTANT_SET_END_DATE_MUTATION, payload.dateEnd)
    commit(mutationTypes.PLANNING_ASSISTANT_SET_SHIFT_TEMPLATE_ITEM_MUTATION, payload.shiftTemplateItem)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_SHIFT_TEMPLATE_ITEM] ({ commit }: { commit: Function }, payload: ShiftTemplate): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_SHIFT_TEMPLATE_ITEM_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_PERIODICITY] ({ commit }: { commit: Function }, payload: PlanningAssistantPeriodicityPayload): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_PERIODICITY_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_EMPLOYEE_LIST_FOR_PLANNING] ({ commit }: { commit: Function }, payload: EmployeeAvailableForPlanning[]): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_EMPLOYEE_LIST_FOR_PLANNING_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_PLANNED_EMPLOYEE_LIST] ({ commit }: { commit: Function }, payload: PlannedEmployee[]): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_PLANNED_EMPLOYEE_LIST_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_TASK_LIST] ({ commit }: { commit: Function }, payload: Task[]): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_TASK_LIST_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_SHIFT_AVAILABILITY] ({ commit }: { commit: Function }, payload: ShiftAvailability): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_SHIFT_AVAILABILITY_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_EMPLOYEE_FOR_EXCHANGE] ({ commit }: { commit: Function }, payload: AutomaticallyPlannedEmployee | EmployeePlaceholder): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_EMPLOYEE_FOR_EXCHANGE_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_DELETE_EMPLOYEE_FOR_EXCHANGE] ({ commit }: { commit: Function }, payload: AutomaticallyPlannedEmployee): void {
    commit(mutationTypes.PLANNING_ASSISTANT_DELETE_EMPLOYEE_FOR_EXCHANGE_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_CURRENT_SHIFT] ({ commit }: { commit: Function }, payload: PlannedEmployee | undefined): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_CURRENT_SHIFT_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_AUTOMATIC_PLANNING_ID] ({ commit }: { commit: Function }, payload: number | undefined): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_AUTOMATIC_PLANNING_ID_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_AUTOMATIC_PLANNING_IS_PROJECT] ({ commit }: { commit: Function }, payload: boolean): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_AUTOMATIC_PLANNING_IS_PROJECT_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_FILTERED_EMPLOYEES_BY_DEPARTMENT] ({ commit }: { commit: Function }, payload: Employee[]): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_FILTERED_EMPLOYEES_BY_DEPARTMENT_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_IS_EDITTING_SERIE] ({ commit }: { commit: Function }, payload: boolean): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_IS_EDITTING_SERIE_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_FREE_PERIODICITY_PLANNING_ID] ({ commit }: { commit: Function }, payload: number | undefined): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_FREE_PERIODICITY_PLANNING_ID_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_PERIODICITY_PLANNED_EMPLOYEE_LIST] ({ commit }: { commit: Function }, payload: PeriodicityPlannedEmployee[]): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_PERIODICITY_PLANNED_EMPLOYEE_LIST_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_ADDITIONAL_FIELDS_LIST] ({ commit }: { commit: Function }, payload: AdditionalGroup[]): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_ADDITIONAL_FIELDS_LIST_MUTATION, payload)
  },

  [actionTypes.PLANNING_ASSISTANT_SET_ADDITIONAL_FIELDS] ({ commit }: { commit: Function }, payload: AdditionalFieldsPayloadItem[]): void {
    commit(mutationTypes.PLANNING_ASSISTANT_SET_ADDITIONAL_FIELDS_MUTATION, payload)
  },

  //</editor-fold>

  async [actionTypes.LOAD_PLAN] ({ rootState }: { rootState: GlobalState }, payload: PlanPayload): Promise<PlanResponse> {
    const response = await axios.get('/api/Planung/GetCalendar', {
      params: {
        'profitCenter': payload.profitCenterId,
        'abteilung': payload.departmentId,
        'sprache': rootState.currentUser!.uiLanguage.syzId,
        'view': payload.view,
        'kalenderDate':  moment(payload.calendarDate, 'DD/MM/YYYY').format('YYYY/MM/DD'),
      }
    })

    return response.data
  },

  async [actionTypes.LOAD_SHIFTS] ({ rootState }: { rootState: GlobalState }, payload: ShiftListPayload): Promise<ShiftPlanResponse | false> {
    // https://syzagam.syzpep.ch/api/WebApi/api/Planung?ProfitCenter=1&Abteilung=2&PlaningGroup=&mitabeiter=&sprache=1&kalenderDate=19/2/2019&view=timelineWeek

    const response = await axios.get('/api/Planung/GetShiftData', {
      params: {
        'profitCenter': payload.profitCenterId,
        'abteilung': payload.departmentId,
        'kalenderDate': moment(payload.calendarDate, 'DD/MM/YYYY').format('YYYY/MM/DD'),
        'view': payload.view
      }
    })

    return response.data
  },

  async [actionTypes.CREATE_SHIFT_NEW] ({ rootState }: { rootState: GlobalState }, payload: { newShiftPayload: CreateShiftPayload, projectId?: number, isFastPlanning?: boolean }): Promise<any> {
    let urlParam: string
    if (payload.projectId) {
      urlParam = 'CreateProjectSeries'
    } else if(payload.newShiftPayload.Einmalig && !payload.isFastPlanning){
      urlParam = 'CreateProjectSeries'
    } else if(payload.isFastPlanning){
      urlParam = 'CreateProjectSeriesFastPlanning'
    } else if(!payload.newShiftPayload.Einmalig) {
      urlParam = 'CreateProjectWithPeriodicity'
    } else {
      urlParam = 'CreateProjectSeries'
    }

    const response = await axios.post(`/api/Planung/${urlParam}`, payload.newShiftPayload)
    return response.data // Array<Shift>
  },

  async [actionTypes.LOAD_SHIFT_TEMPLATE_LIST] ({}, payload: FetchShiftTemplateListPayload): Promise<Array<ShiftTemplate>> {
    // /api/Planung?Abteilung=2&Planungsgruppe=0

    const response = await axios.get('/api/Planung', {
      params: {
        Abteilung: payload.departmentId,
        Planungsgruppe: payload.planningGroupId || 0
      }
    })

    const data: ShiftTemplateResponse = response.data

    return ShiftTemplate.createListFromResponse(data)
  },

  async [actionTypes.LOAD_SHIFT_TEMPLATE_LIST_FILTER] ({}, payload: FetchShiftTemplateListPayload): Promise<Array<ShiftTemplate>> {

    const response = await axios.get('/api/Planung', {
      params: {
        Abteilung: payload.departmentId,
        Planungsgruppe: payload.planningGroupId || 0,
        GrabShiftsForFastPlannig: "1"
      }
    })

    const data: ShiftTemplateResponse = response.data

    return ShiftTemplate.createListFromResponse(data)
  },

  async [actionTypes.FETCH_EMPLOYEE_PLACEHOLDER_LIST] (
    { rootState }: { rootState: GlobalState }, payload: FetchEmployeePlaceholderListPayload
  ): Promise<Array<EmployeePlaceholder>> {
    const response = await axios.get('/api/Planung', {
      params: {
        'schicht': payload.shiftTemplateId,
        'Sprache': rootState.selectedUiLanguage!.syzId
      }
    })

    const data: ShiftElementResponse = response.data
    const companyStructure: CompanyStructure = store.getters[COMPANY_GET_BASIC_COMPANY_STRUCTURE]

    return EmployeePlaceholder.createListFromResponse(data, companyStructure.planningGroupList)
  },

  async [actionTypes.AUTOMATICALLY_PLAN_EMPLOYEES] ({ commit, rootState, getters}: { commit: any,rootState: GlobalState, getters: any }, payload: AutomaticPlanningPayload): Promise<Array<AutomaticallyPlannedEmployee | EmployeePlaceholder>> {

    const response = await axios.post('/api/Planung/CreateProjectSeriesAutomatic', payload)
    const newAutoSerie: CreateShiftNewResponse = response.data

    commit(mutationTypes.PLANNING_ASSISTANT_SET_ADDITIONAL_FIELDS_LIST_MUTATION, AdditionalGroup.createFromResponse(newAutoSerie.ProjectSeries[0].Zusatzfelder))

    const availableEmployeeList: EmployeeAvailableForPlanning[] = getters[PLANNING_ASSISTANT_GET_EMPLOYEE_LIST_FOR_PLANNING]
    const task: Task[] = getters[PLANNING_ASSISTANT_GET_TASK_LIST]

    const data: any = newAutoSerie.ProjectSeries[0].details.map((item: ShiftDetailResponseItem) => {
      const foundEmployee: EmployeeAvailableForPlanning | undefined = availableEmployeeList.find((employee: EmployeeAvailableForPlanning ) => employee.id === item.MitarbeiterID)
      const foundTask: Task | undefined = task.find((task: Task ) => task.id === item.ArbeitID)

      return {
        Zeile: item.Zeile,
        MitarbeiterID: item.MitarbeiterID ? item.MitarbeiterID : null,
        Name: foundEmployee ? foundEmployee.name : undefined,
        Kapazitat: foundEmployee ? foundEmployee.capacity : null,
        Kompetenz: item.KompetenzName ? item.KompetenzName : '',
        Bildung: item.BildungsgradName ? item.BildungsgradName : '',
        ArbeitID: item.ArbeitID ? item.ArbeitID : null,
        Arbeit: foundTask ? foundTask.name : '',
        springer: foundEmployee ? foundEmployee.isFloater : false,
        NotizElement: item.NotizElement ? item.NotizElement : null,
        startDate: item.datumStart,
        endDate: item.datumEnde,
        Planungsgruppe: item.Planungsgruppe ? item.Planungsgruppe : undefined,
        ProjectSerie: newAutoSerie.ProjectSeries[0].ProjektSerieID,
        detailID: item.detailID ? item.detailID : null
      }
    })
    const companyStructure: CompanyStructure = store.getters[COMPANY_GET_BASIC_COMPANY_STRUCTURE]
    return AutomaticallyPlannedEmployee.createListFromResponse(data, companyStructure.planningGroupList)
  },

  async [actionTypes.FETCH_AVAILABLE_EMPLOYEES_FOR_PLANNING] (
    { rootState }: { rootState: GlobalState }, payload: FetchEmployeeListForPlanningPayload
  ): Promise<EmployeeAvailableForPlanning[]> {
    const response = await axios.get('/api/Planung', {
      params: {
        'Abteilung': payload.departmentId,
        'Planungsgruppe': payload.planningGroupId || 0,
        'sprache': rootState.selectedUiLanguage!.syzId
      }
    })

    const data: EmployeeAvailableForPlanningResponse = response.data

    return EmployeeAvailableForPlanning.createListFromResponse(data)
  },

  async [actionTypes.FETCH_TASK_LIST] ({ rootState }: { rootState: GlobalState }) {
    const response = await axios.get('/api/Arbeiten/GetAll')

    const data: GetArbeitenResponse = response.data
    return Task.createListFromResponse(data)
  },

  async [actionTypes.FETCH_EDIT_SHIFT] ({ rootState }: { rootState: GlobalState }, payload: number): Promise<Array<EditShiftPlan>> {
    const response = await axios.get('/api/Planung/GetProjectData', {
      params: {
        'ProjectSerieID': payload,
        'sprache': rootState.selectedUiLanguage!.syzId,
      }
    })

    return EditShiftPlan.createListFromResponse(response.data)
  },

  async [actionTypes.FETCH_EDIT_SINGLE_PROJECT] ({ rootState }: { rootState: GlobalState }, payload: number): Promise<EditShiftPlan | null> {
    const response = await axios.get('/api/Automatic/GetMultiple', {
      params: {
        'sp': 'GetSingleProjectData',
        'i_Projekt': payload,
        'i_Sprache': rootState.selectedUiLanguage!.syzId,
      }
    })

    if(!response.data.IsError && response.data.Data) {
      return EditShiftPlan.createSingleFromResponse(response.data.Data)
    } else {
      return null
    }
  },

  async [actionTypes.UPDATE_SHIFT_PLAN] ({ rootState }: { rootState: GlobalState }, payload: EditShiftNewPayload): Promise<any> {

    const response = await axios.post('/api/Planung/UpdateProjectSeries', payload)

    if (response) {
      return response.data
    } else {
      return false
    }
  },

  async [actionTypes.UPDATE_AUTO_SHIFT_PLAN] ({ rootState }: { rootState: GlobalState }, payload: EditShiftNewPayload): Promise<any> {

    const response = await axios.post('/api/Planung/UpdateProjectSeriesMagic', payload)

    if (response) {
      return response.data
    } else {
      return false
    }
  },

  async [actionTypes.UPDATE_SHIFT_PROJECT] ({ rootState }: { rootState: GlobalState }, payload: EditShiftNewPayload): Promise<any> {

    const response = await axios.post('/api/Planung/UpdateProjectData2', {
      'ProjektID': payload.ProjektID,
      'Abteilung': payload.Abteilung,
      'ReferenzProjektID': payload.ReferenzProjektID,
      'AbonnementID': payload.AbonnementID,
      'Details': payload.DetailsForUpdate,
      'Zusatzfelder': payload.ZusatzfelderForUpdate
    })

    if (response) {
      return response.data
    } else {
      return false
    }
  },

  async [actionTypes.EVENT_DRAG_SAVE] ({ rootState }: { rootState: GlobalState }, payload: EventDropPayload): Promise<any> {
    const response = await axios.post('/api/Planung', null, {
      params: {
        'Id': payload.projectShiftDetailId,
        'Mitarbeiter': payload.employeeId,
        'Start': payload.startDate,
        'Ende': payload.endDate,
        'isDrag': payload.isDrag,
      }
    })

    return response.data
  },

  async [actionTypes.FETCH_CAPACITY_LIST] ({ rootState }: { rootState: GlobalState }, payload: EmployeeCapacityPayload): Promise<Array<EmployeeCapacity>> {
    const response = await axios.post('/api/Planung', payload.employeeIdList, {
      params: {
        'KalenderDate': moment(payload.calendarDate, 'DD/MM/YYYY').format('YYYY/MM/DD'),
        'view': payload.view
      }
    })

    return EmployeeCapacity.createListFromResponse(response.data)
  },
}

export default planningActions
